import React, { useMemo } from 'react';
import DefaultPromptItem from '@components/Copilot/DefaultPromptItem';
import { AgentId } from '@components/Copilot/types';
import { AvCollapseContent } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { arrayEquals } from '@fabric-ui/utils';
import { Box, Skeleton, Typography } from '@mui/material';

const titleStyle = { background: theme => theme.palette.gradients.primary, backgroundClip: 'text', WebkitTextFillColor: 'transparent' };

const MAX_PROMPTS_VISIBLE = 4;

interface IDefaultPrompts {
  firstName?: string;
  prompts: string[];
  onClick: (value: string) => void;
  isLoading?: boolean;
  agentId: AgentId;
  disabled?: boolean;
}

const DefaultPrompts: React.FC<IDefaultPrompts> = ({ firstName = '', prompts, onClick, isLoading = false, agentId, disabled = false }) => {
  const shouldShowHeadline = !!firstName;
  return (
    <Box sx={{ ...flex.colCenter, width: '100%' }}>
      {shouldShowHeadline && (
        <>
          <Typography variant="h3" sx={{ ...titleStyle, mb: 0 }}>
            Hello, {firstName}
          </Typography>
          <Box sx={{ mb: '50px', fontSize: 18, color: 'var(--color-content-base-secondary)' }}>How can I help you today?</Box>
        </>
      )}

      <Box sx={{ ...flex.col, gap: 1.5, width: '100%', maxHeight: '100%' }}>
        {isLoading ? (
          agentId === AgentId.TICKET_AGENT ? (
            <>
              <Skeleton variant="rounded" animation="wave" height="40px" width="100%" />
              <Skeleton variant="rounded" animation="wave" height="40px" width="70%" />
              <Skeleton variant="rounded" animation="wave" height="40px" width="50%" />
            </>
          ) : (
            <LoadingWithSentence isLoading={isLoading} />
          )
        ) : (
          <AvCollapseContent
            maxLines={MAX_PROMPTS_VISIBLE}
            showArrowIcon
            isListMode
            containerSx={{
              ...flex.colItemsCenter,
              gap: 1.5,
              maxHeight: '100%',
              ...(!shouldShowHeadline && { pb: '170px' }),
            }}
            contentSx={{ alignSelf: 'center' }}>
            {prompts.map(prompt => (
              <DefaultPromptItem key={prompt} prompt={prompt} onClick={() => onClick(prompt)} disabled={disabled} />
            ))}
          </AvCollapseContent>
        )}
      </Box>
    </Box>
  );
};

const MemoizedDefaultPrompts = React.memo(DefaultPrompts, (oldProps, newProps) => {
  if (oldProps.prompts.length !== newProps.prompts.length) {
    return false;
  }
  if (oldProps.isLoading !== newProps.isLoading) {
    return false;
  }
  if (oldProps.firstName !== newProps.firstName) {
    return false;
  }
  if (oldProps.disabled !== newProps.disabled) {
    return false;
  }
  return arrayEquals(oldProps.prompts, newProps.prompts);
});

export default MemoizedDefaultPrompts;

const InitializingSentences = [
  'Waking up the agent...',
  'Connecting to the knowledge base...',
  'Preparing the environment...',
  'Almost ready to assist!',
];
const LoadingWithSentence = ({ isLoading }) => {
  const currentSentence = useMemo(() => {
    if (isLoading) {
      const index = Math.floor(Math.random() * InitializingSentences.length);
      return InitializingSentences[index];
    }
    return '';
  }, [isLoading]);

  return (
    <Box sx={{ ...flex.colCenter, gap: 1, color: 'var(--color-content-base-secondary)' }}>
      {currentSentence}
      <Skeleton variant="rounded" animation="wave" height={20} width="80%" />
    </Box>
  );
};
